import React, { useState, useEffect, useRef } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, Polyline } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  FaMapMarkerAlt,
  FaTrash,
  FaPlus,
  FaCar,
  FaHotel,
  FaRunning,
  FaCalculator,
  FaArrowUp,
  FaArrowDown,
} from 'react-icons/fa'
import serviceService from '../services/serviceService'
import customTripService from '../services/customTripService'
import { toast } from 'react-toastify'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Button from './ui/Button'

// Fix for default marker icons in React Leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

// Custom marker icons
const createCustomIcon = (color, number) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 32px; height: 32px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 2px solid white; display: flex; align-items: center; justify-content: center; box-shadow: 0 3px 6px rgba(0,0,0,0.3);">
      <span style="transform: rotate(45deg); color: white; font-weight: bold; font-size: 14px;">${number}</span>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  })
}

// Map Event Handler Component
const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng)
    },
  })
  return null
}

// Calculate distance between two coordinates (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371 // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance = R * c
  return Math.round(distance * 10) / 10 // Round to 1 decimal
}

const MapTripPlanner = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [locations, setLocations] = useState([])
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [locationName, setLocationName] = useState('')
  const [activities, setActivities] = useState([])
  const [hotels, setHotels] = useState([])
  const [cars, setCars] = useState([])
  const [selectedActivity, setSelectedActivity] = useState(null)
  const [selectedHotel, setSelectedHotel] = useState(null)
  const [selectedCar, setSelectedCar] = useState(null)
  const [pricePerKm, setPricePerKm] = useState(10) // Default ₹10 per km
  const [totalDistance, setTotalDistance] = useState(0)
  const [mileageCharges, setMileageCharges] = useState(0)
  const [showReview, setShowReview] = useState(false)
  const [tripName, setTripName] = useState('')
  const [tripDescription, setTripDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [fetchingLocationName, setFetchingLocationName] = useState(false)
  const mapRef = useRef(null)

  // Default center (India)
  const [center, setCenter] = useState([20.5937, 78.9629])

  useEffect(() => {
    loadServices()
  }, [])

  useEffect(() => {
    calculateDistances()
  }, [locations])

  const loadServices = async () => {
    try {
      const [activitiesData, hotelsData, carsData] = await Promise.all([
        serviceService.getAllServices({ type: 'activity', limit: 100 }),
        serviceService.getAllServices({ type: 'hotel', limit: 100 }),
        serviceService.getAllServices({ type: 'car', limit: 100 }),
      ])
      setActivities(activitiesData.services || [])
      setHotels(hotelsData.services || [])
      setCars(carsData.services || [])
    } catch (error) {
      console.error('Error loading services:', error)
    }
  }

  const calculateDistances = () => {
    if (locations.length < 2) {
      setTotalDistance(0)
      setMileageCharges(0)
      return
    }

    let total = 0
    const updatedLocations = locations.map((loc, index) => {
      if (index === 0) {
        return { ...loc, distanceFromPrevious: 0 }
      }
      const prev = locations[index - 1]
      const distance = calculateDistance(prev.lat, prev.lng, loc.lat, loc.lng)
      total += distance
      return { ...loc, distanceFromPrevious: distance }
    })

    setLocations(updatedLocations)
    setTotalDistance(Math.round(total * 10) / 10)
    setMileageCharges(Math.round(total * pricePerKm))
  }

  const fetchLocationName = async (lat, lng) => {
    try {
      setFetchingLocationName(true)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'TravelPlannerApp/1.0',
          },
        },
      )
      const data = await response.json()

      if (data && data.address) {
        // Build a readable location name from address components
        const parts = []

        // Priority: city/town/village
        if (data.address.city) parts.push(data.address.city)
        else if (data.address.town) parts.push(data.address.town)
        else if (data.address.village) parts.push(data.address.village)
        else if (data.address.municipality) parts.push(data.address.municipality)
        else if (data.address.county) parts.push(data.address.county)

        // Add state
        if (data.address.state) parts.push(data.address.state)
        else if (data.address.state_district) parts.push(data.address.state_district)

        // Add country for international locations
        if (data.address.country && data.address.country !== 'India') {
          parts.push(data.address.country)
        }

        const locationName = parts.length > 0 ? parts.join(', ') : data.display_name
        return locationName
      }
      return ''
    } catch (error) {
      console.error('Error fetching location name:', error)
      toast.error('Could not fetch location name. Please enter manually.')
      return ''
    } finally {
      setFetchingLocationName(false)
    }
  }

  const handleMapClick = async (latlng) => {
    setSelectedLocation(latlng)
    setLocationName('🔍 Detecting location...')

    // Fetch location name from coordinates
    const name = await fetchLocationName(latlng.lat, latlng.lng)
    if (name) {
      setLocationName(name)
    } else {
      // Fallback to coordinates
      setLocationName(`Location (${latlng.lat.toFixed(4)}, ${latlng.lng.toFixed(4)})`)
    }
  }

  const addLocation = () => {
    if (!selectedLocation) {
      toast.warning('Please click on the map to select a location')
      return
    }
    if (!locationName.trim()) {
      toast.warning('Please enter a location name')
      return
    }

    const newLocation = {
      id: Date.now(),
      lat: selectedLocation.lat,
      lng: selectedLocation.lng,
      name: locationName,
      distanceFromPrevious: 0,
      activity: null,
      hotel: null,
    }

    const updated = [...locations, newLocation]
    setLocations(updated)
    setSelectedLocation(null)
    setLocationName('')

    // Update map center to new location
    if (mapRef.current) {
      mapRef.current.setView([selectedLocation.lat, selectedLocation.lng], 10)
    }
  }

  const removeLocation = (id) => {
    setLocations(locations.filter((loc) => loc.id !== id))
  }

  const moveLocationUp = (index) => {
    if (index === 0) return
    const newLocations = [...locations]
    ;[newLocations[index - 1], newLocations[index]] = [newLocations[index], newLocations[index - 1]]
    setLocations(newLocations)
  }

  const moveLocationDown = (index) => {
    if (index === locations.length - 1) return
    const newLocations = [...locations]
    ;[newLocations[index], newLocations[index + 1]] = [newLocations[index + 1], newLocations[index]]
    setLocations(newLocations)
  }

  const addActivityToLocation = (locationId, activity) => {
    setLocations(locations.map((loc) => (loc.id === locationId ? { ...loc, activity } : loc)))
  }

  const addHotelToLocation = (locationId, hotel) => {
    setLocations(locations.map((loc) => (loc.id === locationId ? { ...loc, hotel } : loc)))
  }

  const calculateTotalCost = () => {
    let total = Number(mileageCharges) || 0

    // Add activities cost
    locations.forEach((loc) => {
      if (loc.activity) {
        total += Number(loc.activity.base_price) || 0
      }
      if (loc.hotel) {
        total += Number(loc.hotel.base_price) || 0
      }
    })

    // Add car cost
    if (selectedCar) {
      total += Number(selectedCar.base_price) || 0
    }

    return Math.round(total)
  }

  const handleSubmit = async () => {
    if (!tripName.trim()) {
      toast.warning('Please enter a trip name')
      return
    }
    if (locations.length < 2) {
      toast.warning('Please add at least 2 locations to your trip')
      return
    }

    const tripData = {
      tripName,
      tripDescription,
      locations,
      selectedCar,
      totalDistance,
      mileageCharges,
      pricePerKm,
      totalCost: calculateTotalCost(),
    }

    try {
      setSubmitting(true)
      const response = await customTripService.createCustomTrip(tripData)

      if (response.success) {
        toast.success('Trip plan submitted successfully! Admin will review it shortly.')

        // Clear form
        setLocations([])
        setSelectedCar(null)
        setTripName('')
        setTripDescription('')
        setShowReview(false)

        // Redirect to user's trips if logged in
        if (user) {
          setTimeout(() => {
            navigate('/my-trips')
          }, 2000)
        }
      }
    } catch (error) {
      console.error('Error submitting trip:', error)
      toast.error(error.response?.data?.message || 'Error submitting trip plan')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h2 className="text-2xl font-bold mb-4 flex items-center">
          <FaMapMarkerAlt className="mr-2 text-primary-600" />
          Custom Trip Planner with Map
        </h2>
        <p className="text-gray-600 mb-4">
          Click on the map to add locations to your trip. Distances and charges will be calculated
          automatically.
        </p>

        {/* Map Container */}
        <div className="mb-6">
          <div className="h-96 rounded-lg overflow-hidden border-2 border-gray-200">
            <MapContainer
              center={center}
              zoom={5}
              style={{ height: '100%', width: '100%' }}
              ref={mapRef}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapClickHandler onMapClick={handleMapClick} />

              {/* Display selected location */}
              {selectedLocation && (
                <Marker position={[selectedLocation.lat, selectedLocation.lng]}>
                  <Popup>
                    <div>
                      <strong>New Location</strong>
                      <br />
                      Lat: {selectedLocation.lat.toFixed(4)}
                      <br />
                      Lng: {selectedLocation.lng.toFixed(4)}
                    </div>
                  </Popup>
                </Marker>
              )}

              {/* Display all added locations */}
              {locations.map((loc, index) => (
                <Marker
                  key={loc.id}
                  position={[loc.lat, loc.lng]}
                  icon={createCustomIcon(index === 0 ? '#10B981' : '#3B82F6', index + 1)}
                >
                  <Popup>
                    <div className="min-w-48">
                      <strong className="text-lg">{loc.name}</strong>
                      {index === 0 && (
                        <div className="text-green-600 font-semibold">Base Location</div>
                      )}
                      {index > 0 && (
                        <div className="text-blue-600 text-sm">
                          {loc.distanceFromPrevious} km from previous
                        </div>
                      )}
                      {loc.activity && (
                        <div className="text-sm mt-2">
                          <FaRunning className="inline mr-1" />
                          {loc.activity.name}
                        </div>
                      )}
                      {loc.hotel && (
                        <div className="text-sm">
                          <FaHotel className="inline mr-1" />
                          {loc.hotel.name}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* Draw route line */}
              {locations.length > 1 && (
                <Polyline
                  positions={locations.map((loc) => [loc.lat, loc.lng])}
                  color="#3B82F6"
                  weight={3}
                  opacity={0.7}
                />
              )}
            </MapContainer>
          </div>
        </div>

        {/* Add Location Form */}
        {selectedLocation && (
          <div className="card p-4 bg-blue-50 border-2 border-blue-200 mb-6">
            <h3 className="font-bold mb-3 text-blue-900 flex items-center">
              Add Selected Location
              {fetchingLocationName && (
                <span className="ml-2 text-sm text-gray-600 animate-pulse">
                  (Fetching location name...)
                </span>
              )}
            </h3>
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder="Enter location name (e.g., Mumbai, Delhi)"
                  className="input-field w-full"
                  disabled={fetchingLocationName}
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Location name auto-detected. You can edit it to add an alias or custom name.
                </p>
              </div>
              <Button
                onClick={addLocation}
                icon={<FaPlus />}
                disabled={fetchingLocationName}
                className="h-fit"
              >
                Add Location
              </Button>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              📍 Coordinates: {selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)}
            </p>
          </div>
        )}

        {/* Trip Summary */}
        {locations.length > 0 && (
          <div className="card p-4 bg-gray-50 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Trip Summary</h3>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-sm text-gray-600">Total Distance</div>
                  <div className="text-xl font-bold text-blue-600">{totalDistance} km</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">Mileage Charges</div>
                  <div className="text-xl font-bold text-green-600">₹{mileageCharges}</div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <label className="text-sm font-semibold">Price per KM:</label>
              <input
                type="number"
                value={pricePerKm}
                onChange={(e) => {
                  setPricePerKm(Number(e.target.value))
                  setMileageCharges(Math.round(totalDistance * Number(e.target.value)))
                }}
                className="input-field w-32"
                min="1"
              />
              <span className="text-sm text-gray-600">₹ / km</span>
            </div>
          </div>
        )}

        {/* Locations List */}
        {locations.length > 0 && (
          <div className="space-y-4">
            <h3 className="font-bold text-lg mb-3">Your Route ({locations.length} locations)</h3>
            <p className="text-sm text-gray-600 mb-3">
              💡 Use arrows to reorder locations. The top location is always your base/starting
              point.
            </p>
            {locations.map((loc, index) => (
              <div key={loc.id} className="card p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1">
                    {/* Reorder Controls */}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => moveLocationUp(index)}
                        disabled={index === 0}
                        className={`p-1 rounded ${
                          index === 0
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-blue-600 hover:bg-blue-100'
                        }`}
                        title="Move up"
                      >
                        <FaArrowUp size={14} />
                      </button>
                      <button
                        onClick={() => moveLocationDown(index)}
                        disabled={index === locations.length - 1}
                        className={`p-1 rounded ${
                          index === locations.length - 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-blue-600 hover:bg-blue-100'
                        }`}
                        title="Move down"
                      >
                        <FaArrowDown size={14} />
                      </button>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={`${index === 0 ? 'bg-green-600' : 'bg-blue-600'} text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm`}
                        >
                          {index + 1}
                        </span>
                        <h4 className="font-bold text-lg">{loc.name}</h4>
                        {index === 0 && (
                          <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">
                            Base Location
                          </span>
                        )}
                      </div>
                      {index > 0 && (
                        <p className="text-sm text-blue-600 ml-10">
                          {loc.distanceFromPrevious} km from {locations[index - 1].name}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => removeLocation(loc.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <FaTrash />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-10">
                  {/* Activity Selection */}
                  <div>
                    <label className="label flex items-center">
                      <FaRunning className="mr-2" />
                      Activity
                    </label>
                    <select
                      value={loc.activity?.id || ''}
                      onChange={(e) => {
                        const activity = activities.find((a) => a.id === Number(e.target.value))
                        addActivityToLocation(loc.id, activity || null)
                      }}
                      className="input-field text-sm"
                    >
                      <option value="">No activity</option>
                      {activities.map((activity) => (
                        <option key={activity.id} value={activity.id}>
                          {activity.name} - ₹{activity.base_price}
                        </option>
                      ))}
                    </select>
                    {loc.activity && (
                      <p className="text-xs text-gray-600 mt-1">{loc.activity.description}</p>
                    )}
                  </div>

                  {/* Hotel Selection */}
                  <div>
                    <label className="label flex items-center">
                      <FaHotel className="mr-2" />
                      Hotel
                    </label>
                    <select
                      value={loc.hotel?.id || ''}
                      onChange={(e) => {
                        const hotel = hotels.find((h) => h.id === Number(e.target.value))
                        addHotelToLocation(loc.id, hotel || null)
                      }}
                      className="input-field text-sm"
                    >
                      <option value="">No hotel</option>
                      {hotels.map((hotel) => (
                        <option key={hotel.id} value={hotel.id}>
                          {hotel.name} - ₹{hotel.base_price}/night
                        </option>
                      ))}
                    </select>
                    {loc.hotel && (
                      <p className="text-xs text-gray-600 mt-1">{loc.hotel.description}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Car Selection */}
        {locations.length > 0 && (
          <div className="card p-4 mt-6">
            <label className="label flex items-center text-lg mb-3">
              <FaCar className="mr-2" />
              Select Car for Trip
            </label>
            <select
              value={selectedCar?.id || ''}
              onChange={(e) => {
                const car = cars.find((c) => c.id === Number(e.target.value))
                setSelectedCar(car || null)
              }}
              className="input-field"
            >
              <option value="">No car</option>
              {cars.map((car) => (
                <option key={car.id} value={car.id}>
                  {car.name} - ₹{car.base_price}
                </option>
              ))}
            </select>
            {selectedCar && <p className="text-sm text-gray-600 mt-2">{selectedCar.description}</p>}
          </div>
        )}

        {/* Final Review */}
        {locations.length >= 2 && !showReview && (
          <div className="flex justify-center mt-6">
            <Button onClick={() => setShowReview(true)} icon={<FaCalculator />} size="lg">
              Review & Submit Trip Plan
            </Button>
          </div>
        )}

        {showReview && (
          <div className="card p-6 mt-6 bg-gradient-to-br from-blue-50 to-green-50 border-2 border-blue-200">
            <h3 className="text-2xl font-bold mb-4 text-blue-900">Final Trip Review</h3>

            {/* Route Map Visualization */}
            <div className="bg-white p-4 rounded-lg mb-4">
              <h4 className="font-bold mb-3 flex items-center">
                <FaMapMarkerAlt className="mr-2 text-primary-600" />
                Your Complete Route
              </h4>
              <div className="h-80 rounded-lg overflow-hidden border-2 border-gray-200">
                <MapContainer
                  center={locations.length > 0 ? [locations[0].lat, locations[0].lng] : center}
                  zoom={6}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  {/* Display all locations */}
                  {locations.map((loc, index) => (
                    <Marker
                      key={loc.id}
                      position={[loc.lat, loc.lng]}
                      icon={createCustomIcon(index === 0 ? '#10B981' : '#3B82F6', index + 1)}
                    >
                      <Popup>
                        <div className="min-w-48">
                          <strong className="text-lg">{loc.name}</strong>
                          {index === 0 && (
                            <div className="text-green-600 font-semibold text-xs">
                              Base Location
                            </div>
                          )}
                          {index > 0 && (
                            <div className="text-blue-600 text-xs">
                              {loc.distanceFromPrevious} km from previous
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}

                  {/* Draw complete route */}
                  {locations.length > 1 && (
                    <Polyline
                      positions={locations.map((loc) => [loc.lat, loc.lng])}
                      color="#3B82F6"
                      weight={4}
                      opacity={0.8}
                    />
                  )}
                </MapContainer>
              </div>
              <div className="mt-3 text-sm text-gray-600">
                <p>
                  🗺️ Total Route: {totalDistance} km covering {locations.length} locations
                </p>
                <p className="mt-1">
                  📍 Starting from: <span className="font-semibold">{locations[0]?.name}</span>
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="label">Trip Name *</label>
              <input
                type="text"
                value={tripName}
                onChange={(e) => setTripName(e.target.value)}
                placeholder="e.g., North India Heritage Tour"
                className="input-field"
              />
            </div>

            <div className="mb-4">
              <label className="label">Trip Description</label>
              <textarea
                value={tripDescription}
                onChange={(e) => setTripDescription(e.target.value)}
                placeholder="Describe your trip..."
                className="input-field"
                rows="3"
              />
            </div>
            {/* Route Order Summary */}
            <div className="bg-white p-4 rounded-lg mb-4 border-l-4 border-blue-500">
              <h4 className="font-bold mb-3 flex items-center">
                <FaMapMarkerAlt className="mr-2 text-blue-600" />
                Journey Sequence
              </h4>
              <div className="space-y-2">
                {locations.map((loc, idx) => (
                  <div key={loc.id} className="flex items-start gap-3">
                    <div
                      className={`${idx === 0 ? 'bg-green-600' : 'bg-blue-600'} text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0`}
                    >
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold">{loc.name}</div>
                      {idx === 0 && <div className="text-xs text-green-600">Starting Point</div>}
                      {idx > 0 && (
                        <div className="text-xs text-gray-600">
                          {loc.distanceFromPrevious} km from {locations[idx - 1].name}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        {loc.activity && `🎯 ${loc.activity.name}`}
                        {loc.activity && loc.hotel && ' • '}
                        {loc.hotel && `🏨 ${loc.hotel.name}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {selectedCar && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-sm">
                    <FaCar className="text-blue-600" />
                    <span className="font-semibold">Selected Vehicle:</span>
                    <span>{selectedCar.name}</span>
                    <span className="text-gray-600">(₹{selectedCar.base_price})</span>
                  </div>
                </div>
              )}
            </div>
            <div className="bg-white p-4 rounded-lg mb-4">
              <h4 className="font-bold mb-3">Cost Breakdown</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>
                    Mileage ({totalDistance} km × ₹{pricePerKm}/km)
                  </span>
                  <span className="font-semibold">₹{mileageCharges}</span>
                </div>
                {locations.map((loc, idx) => (
                  <React.Fragment key={loc.id}>
                    {loc.activity && (
                      <div className="flex justify-between">
                        <span>Activity at {loc.name}</span>
                        <span className="font-semibold">₹{loc.activity.base_price}</span>
                      </div>
                    )}
                    {loc.hotel && (
                      <div className="flex justify-between">
                        <span>Hotel at {loc.name}</span>
                        <span className="font-semibold">₹{loc.hotel.base_price}</span>
                      </div>
                    )}
                  </React.Fragment>
                ))}
                <div className="border-t-2 border-gray-300 pt-2 flex justify-between text-lg">
                  <span className="font-bold">Total Cost</span>
                  <span className="font-bold text-green-600">₹{calculateTotalCost()}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button variant="secondary" onClick={() => setShowReview(false)} className="flex-1">
                Back to Editing
              </Button>
              <Button onClick={handleSubmit} className="flex-1">
                Submit for Admin Review
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MapTripPlanner
